import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { PROPERTY_KEYS } from '../../config';
import { LoginFormFields } from '../../hooks';
import { getAppInfo } from './appInfo';
import {
  AppInfo,
  APIBaseResponse,
  LoginSuccessResponse,
  NestEnvelope,
} from '../../types';
import { toHttps } from '../../utils/serviceUrl';
import type { propertiesState } from '../features/propertiesSlice';

/**
 * Solo la porción del estado que este endpoint necesita, no `RootState`
 * completo: `RootState` se infiere del store, que registra este mismo
 * `authApi`, así que referenciarlo aquí crea un ciclo de tipos y todo el
 * archivo colapsa a `any` sin ningún error visible en este archivo.
 */
type StateWithProperties = { properties: propertiesState };

/**
 * `queryFn` infiere el tipo de éxito de lo que retorna, no de una anotación
 * implícita como el `query` de arriba — sin este genérico, attemptSlice deja
 * de poder leer `action.payload.user.username` en su matcher de logout.
 */
type LogoutResponse = APIBaseResponse & { user: { username: string } };

type Params = {
  fields: LoginFormFields;
  url: string;
};

type BodyLogin = {
  /**
   * `action` va como string: el `LoginDTO` de NestJS lo valida con
   * `@IsString() @MinLength(1) @MaxLength(1)`. Java lo aceptaba como número;
   * NestJS lo rechazaría con un 400 si se manda `1` en vez de `'1'`.
   */
  action: string
  appInfo: AppInfo,
  password: string,
  type: string,
  username: string | null,
  identificationValue: string | null,
}

export const authApi = createApi({
  reducerPath: 'loginApi',
  baseQuery: fetchBaseQuery(),
  endpoints: builder => ({
    login: builder.mutation<LoginSuccessResponse, Params>({
      query: ({
        fields: { password, username, typeLogin },
        url,
      }: Params) => {

        let body: BodyLogin = {
          action: '1',
          appInfo: getAppInfo(),
          password,
          type: typeLogin,
          username: null,
          identificationValue: null,
        };
        if (typeLogin === '1') {
          body.username = username;
        } else if (typeLogin === '2') {
          body.identificationValue = username;
        }
        return {
          url,
          method: 'POST',
          body
        };
      },
      /**
       * NestJS envuelve toda respuesta exitosa en `{ success, message, data }`
       * (`ResponseLoggerInterceptor`, global) y el `{ appInfo, user }` viaja en
       * `data`. Sin desenvolverlo, `authSlice` leía `appInfo` en el primer
       * nivel y el ingreso correcto lanzaba un `TypeError` en el reducer. Los
       * rechazos no pasan por aquí: llegan como error, con el mismo sobre, y
       * `authSlice` ya los lee dentro de `data`.
       */
      transformResponse: (response: NestEnvelope<LoginSuccessResponse>) =>
        response.data,
    }),
    logout: builder.mutation<LogoutResponse, string>({
      /**
       * Login y logout son el mismo servicio (`action` en el cuerpo decide
       * cuál es), así que la URL se resuelve aquí en vez de recibirla como
       * parámetro: los cuatro sitios que llaman logoutMutation(username) no
       * tienen por qué conocer esa URL para poder cerrar sesión.
       *
       * Antes la ruta era 'v5/login' fija y sin baseUrl configurado — el
       * navegador la resolvía contra el origen de la propia Web (hallazgo M),
       * así que el logout nunca llegaba al backend y la sesión jamás se
       * invalidaba del lado del servidor.
       *
       * Login ya migró a NestJS (`PROPERTY_KEYS.LOGIN`); logout se quedó en el
       * Java legacy porque ahí no existe todavía — usa su propia llave
       * (`PROPERTY_KEYS.LOGOUT`) hasta que el backend lo agregue. Ver
       * docs/reference/contrato-api.md.
       */
      async queryFn(username: string, { getState }, _extraOptions, baseQuery) {
        const { properties } = (getState() as StateWithProperties).properties;
        const url = properties[PROPERTY_KEYS.LOGOUT];

        if (!url) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'No se encontró la URL de sesión en las propiedades.',
            } as FetchBaseQueryError,
          };
        }

        const result = await baseQuery({
          url: toHttps(url),
          method: 'POST',
          body: {
            action: 2,
            appInfo: getAppInfo(),
            username,
          },
        });

        // fetchBaseQuery no valida el cuerpo contra un tipo: el contrato es el
        // mismo que ya se asume, sin validar, en el resto del archivo y en
        // authSlice para esta misma familia de respuesta (login/logout).
        return result as { data: LogoutResponse } | { error: FetchBaseQueryError };
      },
    }),
  }),
});

export const { 
  useLoginMutation, useLogoutMutation } = authApi;
